import { useState } from 'react';
import { Alert, Button, Col, message, Row, Select, Space, Typography } from 'antd';
import { useQueryClient } from 'react-query';
import { useGetPrice, useGetProducts } from '../../../../../../../../Hooks/TT_ELD/Companies';
import { useChangeSubscriptionPrice } from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { apiErrorMessage } from '../../../../../../../../API/TT_ELD/legalMigration';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';
import { priceLabel, useCurrentPrice } from './SubscriptionSummary';

const { Text } = Typography;

// The Stripe product named after IFTA holds the Standard ELD prices (with / without IFTA)
const productLabel = (name: string = '') => (/ifta/i.test(name) ? `${STANDARD_ELD} Product` : name);

// Standard ELD prices: $15–20 are without IFTA, $25 and above are with IFTA
const iftaLabel = (price: any) => {
  const dollars = (price?.unit_amount ?? 0) / 100;
  if (dollars >= 25) return 'With IFTA';
  if (dollars >= 15 && dollars <= 20) return 'Without IFTA';
  return null;
};

const standardPriceLabel = (price: any) => {
  const ifta = iftaLabel(price);
  return ifta ? `${priceLabel(price)} · ${ifta}` : priceLabel(price);
};

interface Props {
  companyId: string;
  company: any;
  // The ELD type the company was just switched to
  toStandard: boolean;
  // false when reopened later from the "Price not set" warning
  justMigrated: boolean;
  onDone(): void;
}

// Shown in the migration modal once the company has been switched: the backend returns legal or
// default products/prices based on the company's (new) ELD type
const PricePicker = ({ companyId, company, toStandard, justMigrated, onDone }: Props) => {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState<string | undefined>();
  const [priceId, setPriceId] = useState<string | undefined>();
  const { data: products, isFetching: productsLoading } = useGetProducts(companyId);
  const { data: prices, isFetching: pricesLoading } = useGetPrice(companyId, productId);
  const changePrice = useChangeSubscriptionPrice(companyId);
  const currentPrice = useCurrentPrice(company, companyId);

  const productOptions = (products?.products ?? []).map((p: any) => ({ label: productLabel(p.name), value: p.id }));
  const priceOptions = (prices?.prices ?? []).map((p: any) => ({ label: toStandard ? standardPriceLabel(p) : priceLabel(p), value: p.id }));
  const selectedPrice = (prices?.prices ?? []).find((p: any) => p.id === priceId);

  const priceRequired = toStandard;
  const canSubmit = !productsLoading && (!priceRequired || !!priceId);
  const from = currentPrice ? priceLabel(currentPrice) : company?.priceId || 'the current plan';
  const to = selectedPrice ? (toStandard ? standardPriceLabel(selectedPrice) : priceLabel(selectedPrice)) : `the ${DEFAULT_ELD} plan`;

  const onSubmit = async () => {
    try {
      await changePrice.mutateAsync(priceId);
      message.success('Subscription price changed');
      queryClient.invalidateQueries(`getPrice/${companyId}`);
      queryClient.invalidateQueries(`subscription/${company?.subscriptionId}`);
      onDone();
    } catch (error) {
      message.error(apiErrorMessage(error));
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {justMigrated && (
        <Alert
          type="success"
          showIcon
          message={`Migration started. The company is now on ${toStandard ? STANDARD_ELD : DEFAULT_ELD}.`}
          description="Drivers and logs keep migrating in the background. Set the subscription price to finish."
        />
      )}
      <Text>
        {toStandard
          ? `Choose the ${STANDARD_ELD} plan (with IFTA or without IFTA). The matching processing fee is added.`
          : `Leave the price empty to move the subscription to the ${DEFAULT_ELD} plan. The ${STANDARD_ELD} processing fee is removed.`}
      </Text>
      <Row gutter={[16, 8]}>
        <Col xs={24} md={12}>
          <Text>Product</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select product"
            allowClear
            loading={productsLoading}
            disabled={productsLoading}
            value={productId}
            onChange={(value) => {
              setProductId(value);
              setPriceId(undefined);
            }}
            options={productOptions}
          />
        </Col>
        <Col xs={24} md={12}>
          <Text>Price{priceRequired ? '' : ' (optional)'}</Text>
          <Select
            style={{ width: '100%' }}
            placeholder={priceRequired ? 'Select price' : `${DEFAULT_ELD} plan`}
            allowClear
            loading={pricesLoading}
            value={priceId}
            onChange={setPriceId}
            options={priceOptions}
            disabled={!productId}
          />
        </Col>
      </Row>
      {canSubmit && (
        <Text type="secondary">
          The Stripe subscription moves from <Text strong>{from}</Text> to <Text strong>{to}</Text>. The quantity (
          {company?.quantity ?? '-'}) stays the same.
        </Text>
      )}
      <div style={{ textAlign: 'right' }}>
        <Button type="primary" disabled={!canSubmit} loading={changePrice.isLoading} onClick={onSubmit}>
          {!toStandard && !priceId ? `Move to ${DEFAULT_ELD} plan` : 'Set price'}
        </Button>
      </div>
    </Space>
  );
};

export default PricePicker;
